import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { finalize, retry, shareReplay, tap } from 'rxjs/operators';

/**
 * Endpoints whose responses do not change within a browsing session. The audit
 * data behind them is republished on a quarterly cycle, so re-fetching them on
 * every navigation only costs the user time.
 */
const CACHEABLE_PATHS: Array<RegExp> = [
  /\/timePeriod(\?|$)/,
  /\/area\/systemLevelTypes(\?|$)/,
  /\/area\/systemLevel(\?|$)/,
  /\/indicator\/priorityGroups(\?|$)/,
  /\/indicator\/tags(\?|$)/,
  /\/indicator\/list(\?|$)/,
  /\/indicator\/\d+\/details(\?|$)/,
  /\/indicator\/pathwayGroup\/\d+(\?|$)/,
  /\/indicator\/indicatorGroup\/\d+(\?|$)/,
  /\/externalResource(\?|$)/,
];

const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry {
  storedAt: number;
  response: HttpResponse<unknown>;
}

const responseCache = new Map<string, CacheEntry>();
// Requests that have gone out but not yet come back. A second caller asking for
// the same URL joins the one in flight instead of opening its own connection,
// which is what stops duplicate lookups such as the two systemLevelTypes calls
// the period/area selector used to make back to back.
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

function deepCopy<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function isCacheable(url: string): boolean {
  return CACHEABLE_PATHS.some(pattern => pattern.test(url));
}

/** Clears cached responses, for use when the underlying data may have changed. */
export function clearApiCache(): void {
  responseCache.clear();
}

export const httpCacheInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  const key = req.urlWithParams;

  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.storedAt < CACHE_TTL_MS) {
    // Deep copy on the way out. Components in this app edit the objects they are
    // handed (appending to IndicatorName, setting filterSelected, hanging chart
    // data off indicators), so handing back the stored instance would let one
    // page's edits leak into the next visit.
    return of(cached.response.clone({ body: deepCopy(cached.response.body) }));
  }
  if (cached) {
    responseCache.delete(key);
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const request$ = next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse && isCacheable(key)) {
        responseCache.set(key, {
          storedAt: Date.now(),
          response: event.clone({ body: deepCopy(event.body) })
        });
      }
    }),
    finalize(() => inFlight.delete(key)),
    // One subscription drives the request; every caller that joined shares the
    // same response rather than re-triggering it. refCount is on deliberately: it
    // lets the underlying request be aborted when the last caller walks away,
    // which is what makes switchMap-style cancellation actually cancel.
    shareReplay({ bufferSize: 1, refCount: true })
  );

  inFlight.set(key, request$);
  return request$;
};

const MAX_RETRIES = 2;

/**
 * Retries transient failures. A 5xx or a status of 0 (network dropped, request
 * cut off) is worth another go; a 4xx means the request itself was wrong and
 * repeating it only delays the error reaching the user.
 */
export const httpRetryInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        const status = error?.status;
        const worthRetrying = status === 0 || (status >= 500 && status < 600);
        if (!worthRetrying) {
          return throwError(() => error);
        }
        return timer(retryCount * 500);
      }
    })
  );
};
